/** /settings/mobile - pair the Memso Mobile phone app, watch run
 *  status, flip per-sensor toggles + a global pause. Self-contained
 *  fetch helpers (same pattern as the work-connectors panel) so this
 *  surface can evolve without touching shared api code. */
import { useCallback, useEffect, useState } from "react";
import { Smartphone, RefreshCw, Power, Trash2 } from "lucide-react";
import { getCsrfToken } from "../../../lib/auth";

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

async function jsonFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(input, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    try {
      throw new Error(String(JSON.parse(text).error || res.statusText));
    } catch {
      throw new Error(text || `Request failed (${res.status})`);
    }
  }
  return (await res.json()) as T;
}

interface SensorRow {
  id: string;
  label: string;
  status: "live" | "stub";
  hint: string;
  enabled: boolean;
}
interface RunRow {
  id: string;
  device_label: string | null;
  status: string;
  created_at: string;
  paired_at: string | null;
  last_batch_at: string | null;
  batch_count: number;
}
interface StatusResp {
  runs: RunRow[];
  paused: boolean;
  sensors: SensorRow[];
}
interface PairResp {
  run_id: string;
  pair_code: string;
  hmac_secret: string;
  pair_expires_at: string;
}

const mobileApi = {
  status: () => jsonFetch<StatusResp>("/api/connectors/mobile/status"),
  pair: () =>
    jsonFetch<PairResp>("/api/connectors/mobile/pair", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: "{}",
    }),
  saveSettings: (body: { paused?: boolean; sensors?: Record<string, boolean> }) =>
    jsonFetch<{ ok: boolean }>("/api/connectors/mobile/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: JSON.stringify(body),
    }),
  revoke: (runId: string) =>
    jsonFetch<{ ok: boolean }>(
      `/api/connectors/mobile/${encodeURIComponent(runId)}/revoke`,
      { method: "POST", headers: { ...csrfHeaders() } },
    ),
};

export default function MobileSection() {
  const [data, setData] = useState<StatusResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pair, setPair] = useState<PairResp | null>(null);

  const refresh = useCallback(async () => {
    try {
      setData(await mobileApi.status());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15_000);
    return () => clearInterval(t);
  }, [refresh]);

  async function connect() {
    setBusy(true);
    try {
      setPair(await mobileApi.pair());
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleSensor(id: string, enabled: boolean) {
    if (!data) return;
    setData({ ...data, sensors: data.sensors.map((s) => (s.id === id ? { ...s, enabled } : s)) });
    try {
      await mobileApi.saveSettings({ sensors: { [id]: enabled } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      refresh();
    }
  }

  async function togglePause(paused: boolean) {
    if (!data) return;
    setData({ ...data, paused });
    try {
      await mobileApi.saveSettings({ paused });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      refresh();
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Smartphone className="w-5 h-5" /> Mobile
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Pair the Memso phone app to weave in location, battery and network as ambient context.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Pair a device
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Open Memso Mobile, tap Connect, and enter this code. Codes expire in 15 minutes.
            </div>
          </div>
          <button
            onClick={connect}
            disabled={busy}
            className="px-3 py-1.5 rounded text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 disabled:opacity-60"
          >
            {busy ? "Working..." : "Connect"}
          </button>
        </div>
        {pair && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/40">
            <div className="text-3xl font-mono font-bold tracking-[0.3em] text-emerald-700 dark:text-emerald-300">
              {pair.pair_code}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Expires {new Date(pair.pair_expires_at).toLocaleTimeString()}. The app exchanges this
              for a signing key it stores in the device keystore.
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
              <Power className="w-4 h-4" /> Pause all collection
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              The app stops sending batches until you switch this back on.
            </div>
          </div>
          <button
            onClick={() => togglePause(!data?.paused)}
            className={`px-3 py-1.5 rounded text-xs font-medium border transition ${
              data?.paused
                ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300"
                : "border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            {data?.paused ? "Paused" : "Active"}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Sensors</div>
        {(data?.sensors ?? []).map((s) => (
          <div
            key={s.id}
            className="rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {s.label}
                {s.status === "stub" && (
                  <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border border-slate-300 text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    soon
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{s.hint}</div>
            </div>
            <button
              disabled={s.status === "stub"}
              onClick={() => toggleSensor(s.id, !s.enabled)}
              className={`px-3 py-1.5 rounded text-xs font-medium border transition flex-shrink-0 ${
                s.enabled
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                  : "border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              } ${s.status === "stub" ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {s.enabled ? "On" : "Off"}
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          Paired devices
          <button onClick={refresh} className="text-slate-400 hover:text-slate-600">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {(data?.runs ?? []).length === 0 && (
          <div className="text-xs text-slate-500 dark:text-slate-400">No devices yet.</div>
        )}
        {(data?.runs ?? []).map((run) => (
          <div
            key={run.id}
            className="rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {run.device_label || run.id.slice(0, 8)}{" "}
                <span
                  className={`text-xs ${
                    run.status === "paired"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-slate-400"
                  }`}
                >
                  {run.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {run.batch_count} batches
                {run.last_batch_at
                  ? ` · last ${new Date(run.last_batch_at).toLocaleString()}`
                  : " · none yet"}
              </div>
            </div>
            <button
              onClick={() => mobileApi.revoke(run.id).then(refresh)}
              className="text-rose-500 hover:text-rose-700 flex-shrink-0"
              title="Revoke"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
